/**
 * /admin/transport-disputes — mediation queue (Phase 6)
 * ------------------------------------------------------
 * Admin sees every order with an open dispute and resolves with one
 * of three actions:
 *   - Release: pay the transporter as normal (status=completed)
 *   - Refund: full refund to buyer (status=refunded)
 *   - Split: partial refund per `buyer_refund_pct` (status=partially_refunded)
 *
 * Stripe refunds (`reverse_transfer=true`) are issued automatically;
 * Armstrong's 10% application fee is also refunded so platform stays
 * neutral on dispute outcomes.
 */
import React, { useState, useEffect, useCallback } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { ShieldWarningIcon as ShieldWarning, MapPinIcon as MapPin, CurrencyDollarIcon as CurrencyDollar } from "@phosphor-icons/react";

export default function AdminTransportDisputes() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showResolved, setShowResolved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(
        `/admin/transport-disputes?open_only=${!showResolved}`,
      );
      setOrders(res.data.orders || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't load disputes.");
    } finally {
      setLoading(false);
    }
  }, [showResolved]);
  useEffect(() => { load(); }, [load]);

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-disputes-page">
        <div className="flex items-baseline justify-between mb-1">
          <h1 className="font-spectral text-4xl tracking-tight flex items-center gap-3">
            <ShieldWarning size={32} weight="duotone" className="text-crimson" />
            Transport disputes
          </h1>
          <label className="text-[12px] text-[#6B6B6B] inline-flex items-center gap-1.5 cursor-pointer" data-testid="toggle-show-resolved">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
            />
            Show resolved
          </label>
        </div>
        <p className="text-[13px] text-[#6B6B6B] mb-6 max-w-2xl">
          Mediation queue. Review evidence, then release (transporter wins), refund (buyer wins), or split.
          Stripe refunds with `reverse_transfer=true` so the transporter's Connect balance is debited atomically.
        </p>

        {loading ? (
          <Skeleton className="h-40" />
        ) : orders.length === 0 ? (
          <p className="text-[14px] text-[#6B6B6B] text-center py-10" data-testid="no-disputes">
            {showResolved ? "No disputes on record." : "No open disputes."}
          </p>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <DisputeCard key={o.id} order={o} onChange={load} />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function DisputeCard({ order, onChange }) {
  const [resolveOpen, setResolveOpen] = useState(false);
  const dispute = order.dispute || {};
  const isResolved = !!dispute.resolved_at;
  return (
    <div
      className={`border rounded-2xl p-5 ${isResolved ? "border-amber-200 bg-amber-50/40" : "border-red-200 bg-red-50/30"}`}
      data-testid={`dispute-card-${order.id}`}
    >
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <p className="font-medium flex items-center gap-1.5">
          <MapPin size={14} weight="duotone" className="text-ink" />
          {order.pickup_location.city}, {order.pickup_location.state}
          <span className="text-[#9A9A9A] mx-1">→</span>
          {order.dropoff_location.city}, {order.dropoff_location.state}
        </p>
        <p className="text-[11px] font-mono text-[#9A9A9A]">{order.id.slice(0, 8)}</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mb-3 text-[12px] text-[#444]">
        <div>
          <p className="text-[10px] uppercase font-bold text-[#9A9A9A]">Buyer</p>
          <p>{order.buyer_email}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase font-bold text-[#9A9A9A]">Transporter</p>
          <p>{order.transporter_display_name || "—"}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase font-bold text-[#9A9A9A]">Order total</p>
          <p><CurrencyDollar size={11} className="inline" weight="duotone" />{(order.amount_usd || 0).toFixed(2)}</p>
        </div>
      </div>
      <div className="border-l-3 border-red-400 pl-3 py-1 mb-2 bg-white/40 rounded">
        <p className="text-[11px] uppercase font-bold text-[#9A9A9A]">
          {dispute.opened_by === "buyer" ? "Buyer" : "Transporter"} report ·{" "}
          {new Date(dispute.opened_at).toLocaleString()}
        </p>
        <p className="text-[13px] text-[#444] mt-0.5 leading-relaxed">"{dispute.reason}"</p>
        {(dispute.evidence_urls || []).length > 0 && (
          <div className="mt-1">
            <p className="text-[10px] uppercase font-bold text-[#9A9A9A]">Evidence</p>
            {(dispute.evidence_urls || []).map((u) => (
              <a key={u} href={u} target="_blank" rel="noreferrer"
                 className="block text-[11px] text-blue-700 underline truncate">
                {u}
              </a>
            ))}
          </div>
        )}
      </div>
      {isResolved ? (
        <div className="bg-amber-100/60 border border-amber-300 rounded p-2 mt-2">
          <p className="text-[11px] uppercase font-bold text-amber-900">
            Resolved by {dispute.resolution?.resolved_by} ·{" "}
            {new Date(dispute.resolved_at).toLocaleString()}
          </p>
          <p className="text-[13px] text-[#444] mt-0.5">
            <strong className="capitalize">{dispute.resolution?.action}</strong>
            {dispute.resolution?.buyer_refund_pct != null && (
              <span> · {dispute.resolution.buyer_refund_pct}% to buyer</span>
            )}
            {dispute.resolution?.note && <span> — "{dispute.resolution.note}"</span>}
          </p>
        </div>
      ) : (
        <Button
          onClick={() => setResolveOpen(true)}
          className="rounded-full bg-ink hover:bg-crimson text-white"
          data-testid={`resolve-trigger-${order.id}`}
        >
          Resolve
        </Button>
      )}
      <ResolveDialog
        open={resolveOpen}
        onOpenChange={setResolveOpen}
        order={order}
        onChange={onChange}
      />
    </div>
  );
}

function ResolveDialog({ open, onOpenChange, order, onChange }) {
  const [action, setAction] = useState("release");
  const [buyerPct, setBuyerPct] = useState(50);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (note.trim().length < 4) {
      toast.error("Resolution note is required.");
      return;
    }
    setSubmitting(true);
    try {
      const body = { action, resolution_note: note };
      if (action === "split") body.buyer_refund_pct = Number(buyerPct);
      const res = await api.post(
        `/admin/transport-disputes/${order.id}/resolve`, body,
      );
      const newStatus = res.data.order?.status;
      toast.success(`Dispute resolved — order now ${newStatus}.`);
      onOpenChange(false);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't resolve.");
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Resolve dispute</DialogTitle></DialogHeader>
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">Outcome</Label>
        <div className="grid grid-cols-3 gap-2 mt-1 mb-3" data-testid="resolve-actions">
          {[
            { v: "release", label: "Release", hint: "transporter wins" },
            { v: "refund", label: "Refund", hint: "buyer wins" },
            { v: "split", label: "Split", hint: "partial each" },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => setAction(opt.v)}
              className={`border rounded-lg p-3 text-left transition-colors ${
                action === opt.v
                  ? "border-ink bg-ink text-white"
                  : "border-[#E5E5E5] hover:bg-[#FAFAFA]"
              }`}
              data-testid={`resolve-action-${opt.v}`}
            >
              <p className="font-bold text-[13px]">{opt.label}</p>
              <p className="text-[10px] opacity-70">{opt.hint}</p>
            </button>
          ))}
        </div>
        {action === "split" && (
          <div className="mb-3">
            <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
              Buyer refund %
            </Label>
            <Input
              type="number" min="0" max="100" step="1"
              value={buyerPct}
              onChange={(e) => setBuyerPct(e.target.value)}
              className="mt-1"
              data-testid="resolve-pct"
            />
            <p className="text-[11px] text-[#9A9A9A] mt-1">
              Refund: ${((order.amount_usd || 0) * (Number(buyerPct) / 100)).toFixed(2)} ·{" "}
              Transporter keeps: ${((order.amount_usd || 0) * (1 - Number(buyerPct) / 100)).toFixed(2)}
            </p>
          </div>
        )}
        <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B]">
          Resolution note (visible to both parties) *
        </Label>
        <Textarea
          value={note} onChange={(e) => setNote(e.target.value)}
          rows={3} className="mt-1"
          placeholder="What the evidence shows and why this is fair…"
          data-testid="resolve-note"
        />
        <Button
          onClick={submit} disabled={submitting}
          className="w-full mt-3 rounded-full bg-crimson hover:bg-ink text-white"
          data-testid="resolve-submit"
        >
          {submitting ? "Resolving…" : `Confirm ${action}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
